import { Card, Col, Row, Tag, Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import type { TablePaginationConfig } from 'antd'
import PageTitle from '../../components/PageTitle'
import { Contact } from '../../components/contact/Contact'
import { ColumnsType } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectSelectedProfile } from '../../store/ProfileReducer'
import { TableParams } from '../../models/TableParams'
import { initialQuery } from '../../models/helper'
import SBTable from '../../components/SBTable'
import { SBRMType } from '../../modules/sbrm/SBRMModel'
import { fieldFormatter } from '../../helpers/formatters'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { extractSortDataFromSorterResult } from '../../helpers/sorter'
import {
  getAnalyticsEvents,
  selectAnalyticsEvents,
  setAnalyticsEventQuery,
} from '../../store/AnalyticsEventReducer'
import { AnalyticsEvent as AnalyticsEventModel } from '../../models/AnalyticsEvent'

const ActivitiesPage = () => {
  dayjs.extend(relativeTime)
  const dispatch = useAppDispatch()

  const { query, isLoading } = useAppSelector((state) => state.analyticsEvent)
  const { isOpen: SBRMIsOpen } = useAppSelector((state) => state.SBRM)
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true)
  const profile = useAppSelector(selectSelectedProfile())
  const items = useAppSelector(selectAnalyticsEvents())

  const setQueryAndGetAnalyticsEvents = (query: TableParams) => {
    dispatch(setAnalyticsEventQuery(query))
    dispatch(getAnalyticsEvents(query))
  }

  const columns: ColumnsType<AnalyticsEventModel> = [
    {
      key: 'id',
      title: '#',
      render: (record: AnalyticsEventModel) => record.id,
    },
    {
      key: 'contact',
      title: 'Audience',
      render: (record: AnalyticsEventModel) => (
        <Contact.Avatar id={record.contact} />
      ),
    },
    {
      key: 'event',
      title: 'Event',
      render: (record: AnalyticsEventModel) => record.event,
    },
    {
      key: 'date',
      title: 'Date',
      render: (record: AnalyticsEventModel) => (
        <Tooltip title={record.date}>
          <Tag>{dayjs(record.date).format('DD/MM/YYYY HH:mm')}</Tag>
        </Tooltip>
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<AnalyticsEventModel>
      | SorterResult<AnalyticsEventModel>[]
  ) => {
    const newQuery = {
      ...query,
      pagination,
      filters: {
        ...query.filters,
        ...filters,
      },
      ...extractSortDataFromSorterResult<AnalyticsEventModel>(sorter),
    }

    setQueryAndGetAnalyticsEvents(newQuery)
  }

  useEffect(() => {
    if (!isFirstRender && SBRMIsOpen) {
      return
    }
    setIsFirstRender(false)

    // Trigger on init and when SBRM is closed
    const baseQuery: TableParams = {
      ...initialQuery,
      filters: { profiles: [profile?.id!] },
      order: 'ascend',
      columnKey: 'name',
    }

    setQueryAndGetAnalyticsEvents(baseQuery)
  }, [dispatch, SBRMIsOpen])

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <PageTitle type="sympa" title="Activity" />
        <Card styles={{ body: { padding: 0 } }} className="big-border-purple">
          <SBTable
            entity={SBRMType.analyticsEvent}
            scroll={{ x: 800 }}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={items}
            pagination={query.pagination}
            loading={isLoading && !items.length}
            onChange={handleTableChange}
            hideCreateButton
          />
        </Card>
      </Col>
    </Row>
  )
}

export default ActivitiesPage
